import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import RecruitmentStatusModal from './components/RecruitmentStatusModal';
import Img from '../../../Img';
import ProspectSectionRow from '../components/ProspectSectionRow';
import { getApiAxiosInstance } from '../../../../utils/network';
import { showErrorPopup } from '../../../../actions/notification-actions';
import {
    PowerFunnelGatedFeatures,
    CppPlayerActions,
} from '../../../Analytics/constants';
import useCollegeProspectAccess from '../../../hooks/useCollegeProspectAccess';
import useCPPTracking from '../../../hooks/useCPPTracking';
import {
    CppRecruitingStatuses,
    EMPTY_STATUS_TEXT,
    RECRUITING_STATUS_ENDPOINT,
    STATUS_LABELS,
} from './constants';
import styles from '../collegeProspectTab.scss';

const TESTID_PREFIX = 'profile.college-prospect.recruitment-status';

const CollegeRecruitmentStatus = () => {
    const profileData = useSelector((state) => state.profile?.data) || {};
    const authUser = useSelector((state) => state.auth?.user);
    const isOwnProfile = !!authUser && !!profileData.memberId && authUser.id === profileData.memberId;
    const dispatch = useDispatch();
    const { hasAnnualPower, openUpgradePopup } = useCollegeProspectAccess();
    const isLocked = !hasAnnualPower;
    const { trackPlayerEngagement } = useCPPTracking();

    const [statusData, setStatusData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        if (!isOwnProfile || !hasAnnualPower) {
            return;
        }
        const fetchStatus = async () => {
            try {
                const response = await getApiAxiosInstance().get(RECRUITING_STATUS_ENDPOINT);
                setStatusData(response.data);
            } catch (err) {
                dispatch(showErrorPopup({
                    message: 'We couldn\'t load your recruitment status. Please try again.',
                    error: err,
                }));
            }
        };
        fetchStatus();
    }, [isOwnProfile, hasAnnualPower, dispatch, refreshKey]);

    const handleSave = () => {
        trackPlayerEngagement(CppPlayerActions.CPP_RECRUITING_STATUS_UPDATED);
        setIsEditing(false);
        setRefreshKey((key) => key + 1);
    };

    const handleEditClick = () => {
        if (isLocked) {
            openUpgradePopup(PowerFunnelGatedFeatures.CPP_RECRUITING_STATUS_LOCKED);
            return;
        }
        setIsEditing(true);
    };

    const handleLockKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleEditClick();
        }
    };

    const statusId = statusData?.statusId ?? null;
    const statusLabel = STATUS_LABELS[statusId] ?? EMPTY_STATUS_TEXT;
    const committedCollegeProgram = statusData?.committedCollegeProgram;

    return (
        <div
            className={styles.academicsSection}
            data-testid={`${TESTID_PREFIX}.container`}
        >
            <div className={styles.academicsHeader}>
                <div className={styles.academicsTitleBlock}>
                    <div
                        className={styles.academicsTitle}
                        data-testid={`${TESTID_PREFIX}.title`}
                    >
                        College Recruitment Status
                    </div>
                    <div
                        className={styles.academicsSubtitle}
                        data-testid={`${TESTID_PREFIX}.subtitle`}
                    >
                        Viewable by college coaches.
                    </div>
                </div>
                {isOwnProfile && (
                    <button
                        type="button"
                        className={styles.academicsEditBtn}
                        onClick={handleEditClick}
                        data-testid={`${TESTID_PREFIX}.edit-btn`}
                    >
                        <Img
                            src="/img/v4/pen.svg"
                            alt=""
                            className={styles.academicsEditIcon}
                        />
                        <span data-testid={`${TESTID_PREFIX}.edit-btn.text`}>Edit</span>
                    </button>
                )}
            </div>

            <div className={styles.academicsCard}>
                <div className={styles.academicsBody}>
                    {isLocked && (
                        <div
                            className={styles.sectionLockRow}
                            role="button"
                            tabIndex={0}
                            onClick={handleEditClick}
                            onKeyDown={handleLockKeyDown}
                            data-testid={`${TESTID_PREFIX}.lock`}
                        >
                            <span className={styles.socialLockBox}>
                                <Img
                                    src="/img/v4/lock.svg"
                                    alt="Locked"
                                    className={styles.socialLockIcon}
                                />
                            </span>
                        </div>
                    )}
                    {!isLocked && (
                        <>
                            <ProspectSectionRow
                                label="Status"
                                value={statusLabel}
                                testIdPrefix={TESTID_PREFIX}
                                testId="status"
                            />
                            {statusId === CppRecruitingStatuses.COMMITTED && committedCollegeProgram?.name && (
                                <ProspectSectionRow
                                    label="Committed To"
                                    value={committedCollegeProgram.name}
                                    testIdPrefix={TESTID_PREFIX}
                                    testId="committed-college"
                                />
                            )}
                        </>
                    )}
                </div>
            </div>

            {isEditing && isOwnProfile && hasAnnualPower && (
                <RecruitmentStatusModal
                    currentStatusId={statusId ?? CppRecruitingStatuses.UNDECIDED}
                    currentCommittedCollegeProgram={committedCollegeProgram ?? null}
                    onClose={() => setIsEditing(false)}
                    onSave={handleSave}
                />
            )}
        </div>
    );
};

export default CollegeRecruitmentStatus;
